import { useMinDelayState } from '@ibsys/kit';
import { CheckIcon, CodeIcon, SearchIcon, TypeIcon, XIcon } from 'lucide-react';
import { ChangeEvent, FC, KeyboardEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { AiInput, IconButton, PredicateIssues } from '@/shared/ui';
import type { SmartSearchInputProps } from './types';

/** Запас справа, чтобы длинный запрос не уходил под кнопки в конце поля; пересчитать при изменении их состава. */
const INPUT_TRAILING_SPACE_CLASS = 'pr-44!';

export const SmartSearchInput: FC<SmartSearchInputProps> = ({
  value,
  onChange,
  mode,
  onModeChange,
  onSearch,
  onClear,
  predicateInfo,
  error,
  isLoading = false,
  placeholder,
  className,
  disabled = false,
}) => {
  const { t } = useTranslation(['dialogs', 'common']);

  const isMagic = useMinDelayState(isLoading, 600);

  // Display value based on mode
  const displayValue = mode === 'nl' ? value.human : value.dsl;

  const startSearch = useCallback(() => {
    onSearch?.();
  }, [onSearch, onSearch]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !isLoading) {
        startSearch();
      }
    },
    [isLoading, startSearch],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (mode === 'nl') {
        onChange({ human: e.target.value || '', dsl: '' });
      } else {
        onChange({ dsl: e.target.value || '', human: '' });
      }
    },
    [mode],
  );

  const toggleMode = useCallback(() => {
    onModeChange(mode === 'nl' ? 'dsl' : 'nl');
  }, [mode, onModeChange]);

  const handleBlur = useCallback(() => {
    const trimmedValue = displayValue.trim();
    if (trimmedValue) {
      startSearch();
    } else {
      onClear();
    }
  }, [displayValue, startSearch, onClear]);

  const handleClear = useCallback(() => {
    onChange({ human: '', dsl: '' });
    onSearch();

    onClear();
  }, [mode, onSearch, onClear, onChange]);

  const issues = predicateInfo?.issues ?? [];
  const hasConversionResult = predicateInfo !== null && predicateInfo !== undefined;

  const renderStatusIcon = () => {
    // Проблемы перечислены под полем, значком отмечаем только успешный разбор
    if (!hasConversionResult || issues.length > 0) {
      return null;
    }

    return (
      <div className="flex size-5 items-center justify-center rounded bg-success">
        <CheckIcon className="size-3 text-success-foreground" />
      </div>
    );
  };

  const defaultPlaceholder =
    mode === 'nl' ? t('dialogs:smartSearch.placeholderNl') : t('dialogs:smartSearch.placeholderDsl');

  const iconRight = (
    <div className="flex gap-2 items-center">
      {renderStatusIcon()}
      {displayValue.trim() && (
        <IconButton
          tabIndex={-1}
          size="sm"
          variant="ghost"
          disabled={isMagic || disabled}
          icon={<XIcon />}
          onClick={handleClear}
          label={t('common:clear')}
        />
      )}
      <IconButton
        tabIndex={-1}
        size="sm"
        variant="ghost"
        disabled={isMagic || disabled}
        icon={mode === 'nl' ? <CodeIcon /> : <TypeIcon />}
        onClick={toggleMode}
        // Способ ввода условия называется одинаково и здесь, и в самих условиях: строгая запись
        // и описание. Названия языков и режимов ввода пользователю ни о чём не говорят.
        label={mode === 'nl' ? t('common:predicateInputSwitchToRecord') : t('common:predicateInputSwitchToDescription')}
      />
      <IconButton
        tabIndex={-1}
        size="sm"
        variant="ghost"
        disabled={isMagic || disabled || !displayValue.trim()}
        icon={<SearchIcon />}
        onClick={onSearch}
        label={t('common:search')}
      />
    </div>
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <AiInput
        iconRight={iconRight}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder ?? defaultPlaceholder}
        isProcessing={isMagic}
        disabled={isLoading || disabled}
        className={cn(mode === 'dsl' && 'font-mono', error && 'border-destructive')}
        classNames={{
          input: INPUT_TRAILING_SPACE_CLASS,
        }}
      />

      {!!error && <div className="text-sm text-destructive">{error}</div>}
      {issues.length > 0 && <PredicateIssues issues={issues} isConditionMissing={!predicateInfo?.dsl} />}
    </div>
  );
};
